import {UI_COLORS} from '@/utils/ui-colors';

const CURRENCY_FORMATTER = new Intl.NumberFormat('de-DE', {style: 'currency', currency: 'EUR'});
const GREEN_BADGE_COLOR = '#11A54D';
const LEGACY_GREEN_BADGE_COLORS = ['#10B981', '#00A944'];

function translateLabel(translate, key, fallback) {
    if (typeof translate === 'function') {
        const translated = translate(key);
        if (translated !== undefined && translated !== null) {
            return translated.toString();
        }
    }

    return fallback;
}

export function getDefaultSettlementStatuses(translate = null) {
    return [
        {value: 'open', label: translateLabel(translate, 'Open', 'Open'), color: UI_COLORS.text.subtle},
        {value: 'billed', label: translateLabel(translate, 'Billed', 'Billed'), color: '#2563EB'},
        {value: 'paid', label: translateLabel(translate, 'Paid', 'Paid'), color: GREEN_BADGE_COLOR},
        {value: 'reversed', label: translateLabel(translate, 'Reversed', 'Reversed'), color: '#EF4444'},
    ];
}

export function createDefaultFinanceEntry() {
    return {
        id: null,
        source_type: 'manual',
        position_id: null,
        position_uuid: null,
        position_title: '',
        service_category_name: '',
        customer: null,
        customer_company_id: null,
        customer_company_name_snapshot: null,
        service_account_id: null,
        customer_service_account_id: null,
        provider_service_account_id: null,
        operator_service_party: null,
        service_relationship: {
            provider: null,
            recipient: null,
        },
        manual_income_company: null,
        manual_income_company_id: null,
        manual_income_company_name_snapshot: null,
        provider_company: null,
        provider_company_id: null,
        provider_company_name_snapshot: null,
        manual_expense_company: null,
        manual_expense_company_id: null,
        manual_expense_company_name_snapshot: null,
        project: null,
        title: '',
        description: null,
        amount_type: 'hourly',
        entry_scope: 'income',
        from_user_id: null,
        to_user_id: null,
        settlement_status: 'open',
        settlement_statuses: getDefaultSettlementStatuses(),
        invoice_number: null,
        billed_at_input: null,
        paid_at_input: null,
        has_reversal: false,
        can_reverse: false,
        can_update: true,
        can_delete: false,
        created_by_user: null,
        updated_by_user: null,
        created_at: null,
        updated_at: null,
        history: [],
        reversal_entry: null,
        quantification_factor: 0,
        quantification_value: 0.25,
        quantity: 1,
        hourly_rate: 0,
        fixed_amount: 0,
        vat_rate: 19,
        internal_note: null,
        customer_base_amount: 0,
        customer_total_amount: 0,
    };
}

export function createDefaultConfirmDialog() {
    return {
        open: false,
        title: '',
        text: '',
        confirmLabel: '',
        confirmButtonClass: 'btn btn-gray mr-2 sm:mr-0',
        pendingAction: null,
    };
}

export function normalizeHexColor(color, fallback) {
    const normalized = String(color || '').trim().toUpperCase();
    if (/^#[0-9A-F]{6}$/.test(normalized)) {
        if (LEGACY_GREEN_BADGE_COLORS.includes(normalized)) {
            return GREEN_BADGE_COLOR;
        }

        return normalized;
    }

    const normalizedFallback = String(fallback || UI_COLORS.text.subtle).toUpperCase();

    return LEGACY_GREEN_BADGE_COLORS.includes(normalizedFallback) ? GREEN_BADGE_COLOR : normalizedFallback;
}

export function hexToRgba(hex, alpha = 1) {
    const normalizedHex = normalizeHexColor(hex, UI_COLORS.text.subtle);
    const red = parseInt(normalizedHex.slice(1, 3), 16);
    const green = parseInt(normalizedHex.slice(3, 5), 16);
    const blue = parseInt(normalizedHex.slice(5, 7), 16);
    const normalizedAlpha = Math.max(0, Math.min(1, Number(alpha)));

    return `rgba(${red}, ${green}, ${blue}, ${normalizedAlpha})`;
}

export function settlementTimelineDotStyle(color) {
    const normalizedColor = normalizeHexColor(color, UI_COLORS.text.subtle);

    return {
        backgroundColor: normalizedColor,
        outline: `6px solid ${hexToRgba(normalizedColor, 0.1)}`,
        outlineOffset: '0px',
    };
}

export function normalizeSettlementStatuses(statuses, translate = null) {
    const defaultMap = getDefaultSettlementStatuses(translate).reduce((carry, item) => {
        carry[item.value] = item;
        return carry;
    }, {});

    const incomingMap = (Array.isArray(statuses) ? statuses : []).reduce((carry, item) => {
        const key = String((item || {}).value || '').toLowerCase();
        if (['open', 'billed', 'paid', 'reversed'].includes(key)) {
            carry[key] = item || {};
        }
        return carry;
    }, {});

    return ['open', 'billed', 'paid', 'reversed'].map((key) => {
        const incoming = incomingMap[key] || {};
        const fallback = defaultMap[key];

        return {
            value: key,
            label: String(incoming.label || fallback.label || key),
            color: normalizeHexColor(incoming.color || fallback.color, fallback.color),
        };
    });
}

export function toDateInput(value) {
    if (!value) {
        return null;
    }

    return moment(value).format('YYYY-MM-DD');
}

export function formatDateDisplay(value) {
    if (!value) {
        return '-';
    }

    return moment(value).format('DD.MM.YYYY');
}

export function formatCurrency(value) {
    return CURRENCY_FORMATTER.format(Number(value || 0));
}

export function formatSignedCurrency(value) {
    const numericValue = Number(value || 0);
    const prefix = numericValue >= 0 ? '+' : '-';
    return `${prefix}${formatCurrency(Math.abs(numericValue))}`;
}
